import { useEffect, useMemo, useState } from 'react';
import {
  CATEGORY_IDS,
  categories as categoryNames,
  type AppSummary,
  type CategoryId,
  type Locale,
} from '@appwin/shared';
import { AppIcon, AppTile, Button, EmptyState, Page, Skeleton } from '@appwin/ui';
import { localePath } from '../config.js';
import { translator } from '../i18n.js';
import { useCatalog, useCategories, useDebounced, useIntersection } from '../api.js';

type Sort = 'recent' | 'name';

export function CatalogPage({
  locale,
  initial,
}: {
  locale: Locale;
  /** Prerendered list. Absent in the browser: the client fetches instead. */
  initial?: AppSummary[];
}) {
  const t = translator(locale);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryId | undefined>(undefined);
  const [sort, setSort] = useState<Sort>('recent');
  const [pages, setPages] = useState<AppSummary[][]>(initial && initial.length > 0 ? [initial] : []);
  const [cursor, setCursor] = useState<string | null>(null);
  const query = useDebounced(search, 250);

  const filter = useMemo(() => ({ q: query || undefined, category, limit: 24 }), [query, category]);
  // Skip the first fetch when the prerendered list is already in place.
  const { data, loading, error } = useCatalog(locale, filter, Boolean(initial?.length));
  const categories = useCategories(locale);

  // A new search or category replaces the list; "load more" appends.
  useEffect(() => {
    if (!data) return;
    setPages([data.items]);
    setCursor(data.nextCursor);
  }, [data]);

  const more = useCatalog(locale, { ...filter, cursor: cursor ?? undefined });
  const sentinel = useIntersection(() => {
    if (!cursor || more.loading || !more.data) return;
    setPages((previous) => [...previous, more.data!.items]);
    setCursor(more.data!.nextCursor);
  });

  const items = useMemo(() => {
    const flat = pages.flat();
    return sort === 'name' ? [...flat].sort((a, b) => a.name.localeCompare(b.name)) : flat;
  }, [pages, sort]);

  const showSkeleton = loading && items.length === 0;
  const activeCategory = category ? categoryNames.categoryName(category, locale) : null;

  return (
    <Page>
      <header className="border-b border-border pb-5 pt-8">
        <h1 className="text-3xl font-semibold tracking-tight">{t('catalog.title')}</h1>
        <p className="mt-1 text-muted">{t('catalog.subtitle')}</p>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <label className="flex-1">
            <span className="sr-only">{t('catalog.searchPlaceholder')}</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('catalog.searchPlaceholder')}
              className="h-11 w-full rounded-xl border border-border bg-surface px-4 text-sm placeholder:text-muted focus:border-accent"
            />
          </label>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value as Sort)}
            aria-label="Сортировка"
            className="h-11 rounded-xl border border-border bg-surface px-3 text-sm"
          >
            <option value="recent">Сначала обновлённые</option>
            <option value="name">По названию</option>
          </select>
        </div>
      </header>

      <nav aria-label={t('catalog.categories')} className="flex flex-wrap gap-2 py-5">
        <CategoryChip
          active={category === undefined}
          onClick={() => setCategory(undefined)}
          label={t('catalog.allCategories')}
        />
        {(categories?.items ?? CATEGORY_IDS.map((id) => ({ id, name: categoryNames.categoryName(id, locale), count: 0 })))
          // Hiding empty categories keeps the row useful while the catalogue is small.
          .filter((entry) => entry.count > 0 || entry.id === category)
          .map((entry) => (
            <CategoryChip
              key={entry.id}
              active={category === entry.id}
              onClick={() => setCategory(entry.id)}
              label={`${entry.name} ${entry.count}`}
            />
          ))}
      </nav>

      {error ? (
        <EmptyState
          title={t('errors.generic')}
          hint={error.message}
          action={
            <Button variant="secondary" onClick={() => setSearch((value) => value)}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : null}

      {showSkeleton ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : null}

      {!error && !showSkeleton && items.length === 0 ? (
        <EmptyState title={t('catalog.empty')} hint={t('catalog.emptyHint')} />
      ) : null}

      {items.length > 0 ? (
        <p className="mb-3 text-xs text-muted">
          {activeCategory ? `${activeCategory} · ` : ''}
          {t('catalog.resultsCount', { count: items.length })}
        </p>
      ) : null}

      <ul className="grid list-none gap-3 p-0 sm:grid-cols-2">
        {items.map((app) => (
          <li key={app.id}>
            <AppTile as="a" href={localePath(locale, `/apps/${app.id}`)}>
              <AppIcon src={app.iconUrl} name={app.name} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{app.name}</p>
                <p className="mt-0.5 line-clamp-2 text-sm text-muted">{app.summary}</p>
                <p className="mt-1.5 text-xs text-muted">
                  {categoryNames.categoryName(app.category, locale)}
                  {app.latestVersion ? ` · ${app.latestVersion}` : ''}
                </p>
              </div>
            </AppTile>
          </li>
        ))}
      </ul>

      {cursor ? (
        <div ref={sentinel} className="py-8 text-center text-sm text-muted">
          {more.loading ? t('common.loading') : t('catalog.loadMore')}
        </div>
      ) : null}
    </Page>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1.5 text-sm transition ${
        active
          ? 'border-accent bg-accent-soft text-accent'
          : 'border-border bg-surface text-muted hover:text-fg'
      }`}
    >
      {label}
    </button>
  );
}
